// GuidedTour — the LIVE guided tour (product tour / spotlight coach-marks).
//
// Promoted from sketches/guided-tour. Where the sketch spotlit a FAKED frame,
// this spotlights the REAL app: the host measures the on-screen targets (the
// composer's "+", the composer, the activity rail), the window and the popup
// itself, and hands those sizes in as props; `resolvePopupPosition` keeps the
// popup inside the window. This component is PURE/presentational — it renders the
// `phase` it is handed at the measured positions and reports intents UP
// (back / next / skip). No side effects, no DOM reads, no `void fn()`; the
// step index and the measurement live in the impure host (CoachHost).
//
// Owner constraints that shape it:
//   1. OPT-IN — it only ever starts from an explicit click ("Take a guided
//      tour" on the landing, D28); never hijacks first load (Dm9).
//   2. Cancellable ANY time — an always-present × on every step + a
//      scrim-click, one click, never punished; every route lands on idle.

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { CSSProperties, ReactNode, Ref } from "react";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import "./GuidedTour.css";

// A surface the tour can spotlight. A CLOSED named set — one entry per real
// target the host measures, so a step can only point at something that exists.
export type TourTargetId = "attach" | "composer" | "rail";

// Which edge of the target the coach-mark popup opens against. Presentational
// metadata carried by the step so the popup grows toward the screen interior.
export type PopupPlacement = "top" | "bottom" | "left" | "right";

// One coach-mark: the target it spotlights + the copy shown beside it. Small,
// named, no loose bag. `title` is the one-line heading; `body` the one-or-two
// line explanation (both localized by the host that builds the Tour).
export type TourStep = {
    readonly target: TourTargetId;
    readonly placement: PopupPlacement;
    readonly title: string;
    readonly body: string;
};

// The tour is an ordered walk of steps. A record type (not a bare array) so the
// shape has a name and can grow fields (id, name) without touching call sites.
export type Tour = {
    readonly steps: readonly TourStep[];
};

// What the tour is showing right now: a spotlight step (carries the index), or
// idle (nothing overlaid). There is no finale screen: the last step's "OK" ends
// the tour directly, the same terminal route as a skip / scrim-click.
export type TourPhase =
    | { readonly type: "step"; readonly index: number }
    | { readonly type: "idle" };

// A measured target rectangle in viewport coordinates (fixed-positioning
// space). Plain serialisable numbers — the host measures the real element via
// getBoundingClientRect at the impure edge and hands the pure component this
// snapshot.
export type TargetRect = {
    readonly top: number;
    readonly left: number;
    readonly width: number;
    readonly height: number;
};

// A width × height in CSS pixels (the window, or the popup itself).
export type Size = {
    readonly width: number;
    readonly height: number;
};

// Everything the host measures for the tour, in one snapshot: the target
// rects, the window size, and the popup's own rendered size (`null` until the
// popup has been measured once).
export type TourLayout = {
    // A target absent here (not yet measured / not in the DOM) makes its step
    // render with no spotlight ring; the popup still shows (centred) so the
    // tour never dead-ends.
    readonly rects: Partial<Record<TourTargetId, TargetRect>>;
    readonly viewport: Size;
    readonly popup: Size | null;
};

export type GuidedTourProps = {
    readonly tour: Tour;
    readonly phase: TourPhase;
    // Host measurements; `null` before the first measurement (popup centred).
    readonly layout: TourLayout | null;
    // Lets the host measure the popup's rendered size.
    readonly popupRef: Ref<HTMLDivElement>;
    // Step controls. `onBack` is a no-op at the first step (the button is
    // disabled there); `onNext` advances between steps. The last step shows
    // "OK", which ends the tour via `onSkip`, so `onNext` is never called there.
    readonly onBack: () => void;
    readonly onNext: () => void;
    // Ends the tour: the ×, a scrim-click, and the last step's "OK".
    readonly onSkip: () => void;
};

// How far the spotlight ring sits outside the measured target, and the gap
// between the target and its popup. Presentational constants.
const SPOTLIGHT_PAD = 6;
const POPUP_GAP = 12;
// The popup never comes closer than this to a window edge. Matches
// `--space-2`, which the CSS safety net (max-width / max-height) uses too.
export const VIEWPORT_MARGIN = 8;
// How close the arrow may get to the popup's own corners (keeps it off the
// rounded corner).
const ARROW_INSET = 16;
// The popup's size before the host has measured it (CSS width + a typical
// height). Only used for the first frame of a step.
const ESTIMATED_POPUP: Size = { width: 260, height: 160 };

// The ring box: fixed at the padded target rect. The dim + ring are drawn by
// CSS (a huge box-shadow spread carves the "hole"); this only sizes it.
const ringStyle = (rect: TargetRect): CSSProperties => ({
    top: rect.top - SPOTLIGHT_PAD,
    left: rect.left - SPOTLIGHT_PAD,
    width: rect.width + SPOTLIGHT_PAD * 2,
    height: rect.height + SPOTLIGHT_PAD * 2,
});

// ——— popup placement (pure) ———————————————————————————————————————————————
// Where the popup goes, given the target, the preferred side, the popup's own
// size and the window size. Tries the preferred side, then the opposite, then
// the other two; the first side where the popup fits fully inside the window
// wins. If none fits, the side with the most room is used and the box is
// clamped into the window (it may then cover part of the target). The arrow
// follows the target's centre, kept inside the popup.

export type PopupPosition = {
    readonly placement: PopupPlacement;
    readonly top: number;
    readonly left: number;
    // Distance from the popup's left (top/bottom) or top (left/right) edge to
    // the arrow's centre.
    readonly arrowOffset: number;
};

const clamp = (value: number, min: number, max: number): number =>
    Math.max(min, Math.min(value, max));

const opposite = (placement: PopupPlacement): PopupPlacement => {
    switch (placement) {
        case "top":
            return "bottom";
        case "bottom":
            return "top";
        case "left":
            return "right";
        case "right":
            return "left";
        default:
            return exhaustSwitchCase(placement);
    }
};

const isVertical = (placement: PopupPlacement): boolean =>
    placement === "top" || placement === "bottom";

// Preferred, opposite, then the two sides on the other axis.
const placementOrder = (
    preferred: PopupPlacement,
): readonly PopupPlacement[] => {
    const crossAxis: readonly PopupPlacement[] = isVertical(preferred)
        ? ["right", "left"]
        : ["bottom", "top"];
    return [preferred, opposite(preferred), ...crossAxis];
};

// The unclamped box for one side: centred on the target along the cross axis.
const idealBox = (
    target: TargetRect,
    placement: PopupPlacement,
    popup: Size,
): { readonly top: number; readonly left: number } => {
    const centreX = target.left + target.width / 2 - popup.width / 2;
    const centreY = target.top + target.height / 2 - popup.height / 2;
    switch (placement) {
        case "top":
            return {
                top: target.top - POPUP_GAP - popup.height,
                left: centreX,
            };
        case "bottom":
            return {
                top: target.top + target.height + POPUP_GAP,
                left: centreX,
            };
        case "left":
            return {
                top: centreY,
                left: target.left - POPUP_GAP - popup.width,
            };
        case "right":
            return {
                top: centreY,
                left: target.left + target.width + POPUP_GAP,
            };
        default:
            return exhaustSwitchCase(placement);
    }
};

// Free space on one side of the target, inside the window margins.
const roomOn = (
    target: TargetRect,
    placement: PopupPlacement,
    viewport: Size,
    margin: number,
): number => {
    switch (placement) {
        case "top":
            return (
                Math.min(target.top - POPUP_GAP, viewport.height - margin) -
                margin
            );
        case "bottom":
            return (
                viewport.height -
                margin -
                Math.max(target.top + target.height + POPUP_GAP, margin)
            );
        case "left":
            return (
                Math.min(target.left - POPUP_GAP, viewport.width - margin) -
                margin
            );
        case "right":
            return (
                viewport.width -
                margin -
                Math.max(target.left + target.width + POPUP_GAP, margin)
            );
        default:
            return exhaustSwitchCase(placement);
    }
};

const sizeAlong = (placement: PopupPlacement, popup: Size): number =>
    isVertical(placement) ? popup.height : popup.width;

// The box fits on a side when the main axis needs no clamping and the cross
// axis can be shifted into the window.
const fits = (
    target: TargetRect,
    placement: PopupPlacement,
    popup: Size,
    viewport: Size,
    margin: number,
): boolean => {
    const box = idealBox(target, placement, popup);
    const insideX =
        box.left >= margin && box.left + popup.width <= viewport.width - margin;
    const insideY =
        box.top >= margin && box.top + popup.height <= viewport.height - margin;
    const crossFits = isVertical(placement)
        ? popup.width <= viewport.width - 2 * margin
        : popup.height <= viewport.height - 2 * margin;
    return crossFits && (isVertical(placement) ? insideY : insideX);
};

export const resolvePopupPosition = (
    target: TargetRect,
    preferred: PopupPlacement,
    popup: Size,
    viewport: Size,
    margin: number = VIEWPORT_MARGIN,
): PopupPosition => {
    const order = placementOrder(preferred);
    const fitting = order.find((p) => fits(target, p, popup, viewport, margin));
    // Nothing fits: the side with the most room relative to the popup's size
    // (earlier sides win ties).
    const roomiest = order.reduce((best, p) =>
        roomOn(target, p, viewport, margin) / sizeAlong(p, popup) >
        roomOn(target, best, viewport, margin) / sizeAlong(best, popup)
            ? p
            : best,
    );
    const placement = fitting ?? roomiest;
    const box = idealBox(target, placement, popup);
    const top = clamp(box.top, margin, viewport.height - margin - popup.height);
    const left = clamp(box.left, margin, viewport.width - margin - popup.width);
    const arrowOffset = isVertical(placement)
        ? clamp(
              target.left + target.width / 2 - left,
              Math.min(ARROW_INSET, popup.width / 2),
              Math.max(popup.width - ARROW_INSET, popup.width / 2),
          )
        : clamp(
              target.top + target.height / 2 - top,
              Math.min(ARROW_INSET, popup.height / 2),
              Math.max(popup.height - ARROW_INSET, popup.height / 2),
          );
    return { placement, top, left, arrowOffset };
};

const arrowStyle = (position: PopupPosition): CSSProperties =>
    isVertical(position.placement)
        ? { left: position.arrowOffset }
        : { top: position.arrowOffset };

// ——— the coach-mark popup ————————————————————————————————————————————————
// The ONE lifted element per step: a small title + body, a step indicator,
// Back / Next, and the always-present × (skip).

type CoachMarkProps = {
    readonly step: TourStep;
    readonly index: number;
    readonly total: number;
    // `null` when there is nothing to anchor to: the CSS centres the popup.
    readonly position: PopupPosition | null;
    readonly popupRef: Ref<HTMLDivElement>;
    readonly onBack: () => void;
    readonly onNext: () => void;
    readonly onSkip: () => void;
};

const CoachMark = ({
    step,
    index,
    total,
    position,
    popupRef,
    onBack,
    onNext,
    onSkip,
}: CoachMarkProps): ReactNode => {
    const { t } = useLingui();
    const isLastStep = index === total - 1;
    return (
        <div
            ref={popupRef}
            className={
                position === null
                    ? "gt-popup"
                    : `gt-popup gt-popup--${position.placement}`
            }
            style={
                position === null
                    ? undefined
                    : {
                          top: position.top,
                          left: position.left,
                          transform: "none",
                      }
            }
            role="dialog"
            aria-label={step.title}
        >
            {position === null ? null : (
                <div
                    className="gt-popup__arrow"
                    style={arrowStyle(position)}
                    aria-hidden
                />
            )}
            <div className="gt-popup__content">
                <div className="gt-popup__head">
                    <h2 className="gt-popup__title">{step.title}</h2>
                    <IconButton
                        label={t`Skip tour`}
                        size="sm"
                        className="gt-popup__x"
                        onClick={onSkip}
                    >
                        <Icon name="close" size="sm" />
                    </IconButton>
                </div>
                <p className="gt-popup__body">{step.body}</p>
                <div className="gt-popup__foot">
                    <span className="gt-popup__count">
                        {t`${index + 1} of ${total}`}
                    </span>
                    <div className="gt-popup__nav">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onBack}
                            disabled={index === 0}
                        >
                            {t`Back`}
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={isLastStep ? onSkip : onNext}
                        >
                            {isLastStep ? t`OK` : t`Next`}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ——— the component ———————————————————————————————————————————————————————
// A pure switch over the phase, positioned over the REAL app via the measured
// rects. `step` lays a scrim (the spotlight carves the hole); `idle` renders
// nothing. A scrim-click anywhere cancels — cancellation is never punished.

export const GuidedTour = ({
    tour,
    phase,
    layout,
    popupRef,
    onBack,
    onNext,
    onSkip,
}: GuidedTourProps): ReactNode => {
    if (phase.type === "idle") return null;
    const step = tour.steps[phase.index];
    if (step === undefined) return null;
    const rect = layout?.rects[step.target];
    const position =
        layout === null || rect === undefined
            ? null
            : resolvePopupPosition(
                  rect,
                  step.placement,
                  layout.popup ?? ESTIMATED_POPUP,
                  layout.viewport,
              );

    return (
        <>
            <div className="gt-scrim" onClick={onSkip} aria-hidden />
            {rect !== undefined ? (
                <div
                    className="gt-spotlight"
                    style={ringStyle(rect)}
                    aria-hidden
                />
            ) : null}
            <CoachMark
                step={step}
                index={phase.index}
                total={tour.steps.length}
                position={position}
                popupRef={popupRef}
                onBack={onBack}
                onNext={onNext}
                onSkip={onSkip}
            />
        </>
    );
};
